import React, { useEffect, useMemo, useState } from 'react';
import { fetchPipelineInventory, refreshPipeline, type CanonicalPipeline, type RefreshEvidence } from '../lib/pipeline-inventory-api';
import { PIPELINE_METHODOLOGY } from '../lib/pipeline-methodology';
import { useTheme } from '../context/ThemeContext';
import { designTokens } from '../styles/design-tokens';

// Must stay in sync with EBB_SCRAPER_MAP in api-server/routes/admin.js
const SUPPORTED_CODES = new Set(['EGTS', 'TCO', 'MVP', 'TW', 'EPNG', 'GCX', 'PHP', 'NGPL', 'TGP', 'TE', 'EQT', 'GS']);

type RefreshState =
  | { status: 'idle' }
  | { status: 'running' }
  | { status: 'success'; evidence: RefreshEvidence }
  | { status: 'message'; text: string; sourceUrl?: string | null };

type Staleness = 'fresh' | 'aging' | 'stale';

function staleness(days: number | null): Staleness {
  if (days === null) return 'stale';
  if (days < 30) return 'fresh';
  if (days < 90) return 'aging';
  return 'stale';
}

const STALE_COLOR: Record<Staleness, string> = {
  fresh: '#10b981',
  aging: '#f59e0b',
  stale: '#ef4444',
};

const STALE_BG: Record<Staleness, string> = {
  fresh: 'transparent',
  aging: 'rgba(245, 158, 11, 0.06)',
  stale: 'rgba(239, 68, 68, 0.05)',
};

interface Props {
  onBack: () => void;
}

export function PipelineInventoryPage({ onBack }: Props) {
  const { theme } = useTheme();
  const [rows, setRows] = useState<CanonicalPipeline[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [operatorFilter, setOperatorFilter] = useState<string>('All');
  const [refreshStates, setRefreshStates] = useState<Record<number, RefreshState>>({});

  useEffect(() => {
    fetchPipelineInventory()
      .then(setRows)
      .catch((e) => setError(e.message));
  }, []);

  const operators = useMemo(() => {
    if (!rows) return ['All'];
    return ['All', ...Array.from(new Set(rows.map((r) => r.operator))).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    return operatorFilter === 'All' ? rows : rows.filter((r) => r.operator === operatorFilter);
  }, [rows, operatorFilter]);

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setRowState = (id: number, state: RefreshState) =>
    setRefreshStates((prev) => ({ ...prev, [id]: state }));

  const handleRefresh = async (e: React.MouseEvent, p: CanonicalPipeline) => {
    e.stopPropagation(); // don't toggle expand
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('pp-auth') ?? undefined : undefined;
    setRowState(p.id, { status: 'running' });
    try {
      const result = await refreshPipeline(p.id, token);
      setRows((prev) =>
        prev ? prev.map((r) => (r.id === p.id ? result.pipeline : r)) : prev
      );
      setRowState(p.id, { status: 'success', evidence: result.evidence });
      setTimeout(() => setRowState(p.id, { status: 'idle' }), 12000);
    } catch (err: unknown) {
      const e = err as { message?: string; status?: number; stderr?: string; sourceUrl?: string };
      if (e.status === 429) {
        const text = e.message ?? 'Cooldown active';
        setRowState(p.id, { status: 'message', text, sourceUrl: e.sourceUrl });
        setTimeout(() => setRowState(p.id, { status: 'idle' }), 5000);
      } else {
        const text = e.message ?? 'Failed';
        if (e.stderr) console.error(`[pipeline-refresh] stderr for ${p.pipeline_code}:\n`, e.stderr);
        setRowState(p.id, { status: 'message', text: `Failed — ${text}`, sourceUrl: e.sourceUrl });
        setTimeout(() => setRowState(p.id, { status: 'idle' }), 8000);
      }
    }
  };

  const cellPad = '8px 12px';
  const headerStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: cellPad,
    background: theme.backgroundSecondary,
    fontWeight: 600,
    fontSize: designTokens.typography.fontSize.sm,
    borderBottom: `1px solid ${theme.border}`,
  };
  const cellStyle: React.CSSProperties = {
    padding: cellPad,
    borderBottom: `1px solid ${theme.border}`,
    fontSize: designTokens.typography.fontSize.sm,
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.background, color: theme.text }}>
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: designTokens.typography.fontSize.xl }}>Pipeline Inventory</h1>
        <a href="/admin" onClick={(e) => { e.preventDefault(); onBack(); }} style={{ color: designTokens.colors.brand }}>
          ← Back to admin
        </a>
      </div>

      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <label style={{ fontSize: designTokens.typography.fontSize.sm }}>
          Operator:{' '}
          <select
            value={operatorFilter}
            onChange={(e) => setOperatorFilter(e.target.value)}
            style={{ padding: '4px 8px', background: theme.backgroundSecondary, color: theme.text, border: `1px solid ${theme.border}` }}
          >
            {operators.map((op) => (
              <option key={op} value={op}>{op}</option>
            ))}
          </select>
        </label>
        {rows && (
          <span style={{ fontSize: designTokens.typography.fontSize.sm, color: theme.textSecondary }}>
            {filtered.length} / {rows.length} pipelines
          </span>
        )}
      </div>

      {error && (
        <div style={{ padding: 16, background: STALE_BG.stale, color: '#dc2626' }}>
          Failed to load: {error}
        </div>
      )}

      {!rows && !error && (
        <div style={{ padding: 32, textAlign: 'center', color: theme.textSecondary }}>Loading…</div>
      )}

      {rows && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={headerStyle}>Pipeline</th>
              <th style={headerStyle}>Operator</th>
              <th style={headerStyle}>Diameter (in)</th>
              <th style={headerStyle}>Capacity (MMcf/d)</th>
              <th style={headerStyle}>Latest Util %</th>
              <th style={headerStyle}>Last Verified</th>
              <th style={headerStyle}>Status</th>
              <th style={headerStyle}>Refresh</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const isExpanded = expanded.has(p.id);
              const s = staleness(p.days_since_verified);
              const cap = p.latest_design_capacity_mmcf ?? p.capacity_mmcf_day_max;
              const util = p.latest_utilization_pct ?? p.utilization_pct_avg;
              return (
                <React.Fragment key={p.id}>
                  <tr
                    onClick={() => toggle(p.id)}
                    style={{ cursor: 'pointer', background: STALE_BG[s] }}
                  >
                    <td style={cellStyle}>{p.display_name ?? p.pipeline_name}</td>
                    <td style={cellStyle}>{p.operator}</td>
                    <td style={cellStyle}>{p.max_diameter_in ?? '—'}</td>
                    <td style={cellStyle}>{cap != null ? Number(cap).toLocaleString() : '—'}</td>
                    <td style={cellStyle}>{util != null ? `${Number(util).toFixed(0)}%` : '—'}</td>
                    <td style={cellStyle}>
                      {p.last_verified_at ? `${p.days_since_verified}d ago` : 'never'}
                    </td>
                    <td style={cellStyle}>
                      <span
                        title={s}
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: STALE_COLOR[s],
                        }}
                      />
                    </td>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const rs = refreshStates[p.id] ?? { status: 'idle' };
                        if (!p.pipeline_code) {
                          return (
                            <button disabled title="No pipeline_code set"
                              style={{ padding: '3px 8px', cursor: 'not-allowed', opacity: 0.4 }}>
                              Refresh
                            </button>
                          );
                        }
                        if (!SUPPORTED_CODES.has(p.pipeline_code)) {
                          return (
                            <button disabled title="No scraper available"
                              style={{ padding: '3px 8px', cursor: 'not-allowed', opacity: 0.4 }}>
                              Refresh
                            </button>
                          );
                        }
                        if (rs.status === 'running') {
                          return (
                            <button disabled style={{ padding: '3px 8px', cursor: 'not-allowed', opacity: 0.6 }}>
                              Running…
                            </button>
                          );
                        }
                        if (rs.status === 'success') {
                          const ev = rs.evidence;
                          const rowLabel = ev.rowsAdded > 0
                            ? `+${ev.rowsAdded} rows`
                            : ev.rowsAdded === 0
                            ? '0 new rows'
                            : `${ev.rowsAdded} rows`;
                          const dayLabel = ev.gasDayAfter === ev.gasDayBefore
                            ? `same gas_day ${ev.gasDayAfter ?? '∅'}`
                            : `gas_day ${ev.gasDayBefore ?? '∅'} → ${ev.gasDayAfter ?? '∅'}`;
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: designTokens.typography.fontSize.sm, color: theme.textSecondary }}>
                              <span>{rowLabel}</span>
                              <span>·</span>
                              <span>{dayLabel}</span>
                              {ev.sourceUrl && (
                                <>
                                  <span>·</span>
                                  <a href={ev.sourceUrl} target="_blank" rel="noreferrer" style={{ color: designTokens.colors.brand }}>Verify ↗</a>
                                </>
                              )}
                            </div>
                          );
                        }
                        if (rs.status === 'message') {
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: designTokens.typography.fontSize.sm, color: theme.textSecondary }}>
                              <span>{rs.text}</span>
                              {rs.sourceUrl && (
                                <>
                                  <span>·</span>
                                  <a href={rs.sourceUrl} target="_blank" rel="noreferrer" style={{ color: designTokens.colors.brand }}>Verify ↗</a>
                                </>
                              )}
                            </div>
                          );
                        }
                        return (
                          <button
                            onClick={(e) => handleRefresh(e, p)}
                            style={{ padding: '3px 8px', cursor: 'pointer' }}
                          >
                            Refresh
                          </button>
                        );
                      })()}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={8} style={{ padding: '16px 24px', background: theme.backgroundSecondary, borderBottom: `1px solid ${theme.border}` }}>
                        <dl style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px 16px', margin: 0 }}>
                          <dt style={{ fontWeight: 600 }}>Sources</dt>
                          <dd style={{ margin: 0 }}>
                            {p.diameter_source_url && <a href={p.diameter_source_url} target="_blank" rel="noreferrer" style={{ marginRight: 12 }}>diameter</a>}
                            {p.capacity_source_url && <a href={p.capacity_source_url} target="_blank" rel="noreferrer" style={{ marginRight: 12 }}>capacity</a>}
                            {p.maop_source_url && <a href={p.maop_source_url} target="_blank" rel="noreferrer" style={{ marginRight: 12 }}>MAOP</a>}
                            {!p.diameter_source_url && !p.capacity_source_url && !p.maop_source_url && '—'}
                          </dd>

                          <dt style={{ fontWeight: 600 }}>EBB</dt>
                          <dd style={{ margin: 0 }}>
                            {p.pipeline_code ? <code style={{ marginRight: 8 }}>{p.pipeline_code}</code> : <span style={{ color: theme.textSecondary, marginRight: 8 }}>no code</span>}
                            {p.ebb_endpoint
                              ? <a href={p.ebb_endpoint} target="_blank" rel="noreferrer">{p.ebb_endpoint}</a>
                              : '—'}
                            {' '}({p.ebb_scraper_status ?? 'never'})
                            {p.latest_gas_day && <span style={{ color: theme.textSecondary }}>{' '}— last flow {p.latest_gas_day}</span>}
                          </dd>

                          <dt style={{ fontWeight: 600 }}>How we verify</dt>
                          <dd style={{ margin: 0 }}>
                            {(() => {
                              const m = p.pipeline_code ? PIPELINE_METHODOLOGY[p.pipeline_code] : null;
                              if (!m) return <span style={{ color: theme.textSecondary }}>—</span>;
                              const labelStyle: React.CSSProperties = { color: theme.textSecondary, fontWeight: 600 };
                              const rowStyle: React.CSSProperties = { marginBottom: 2 };
                              return (
                                <div style={{ fontSize: designTokens.typography.fontSize.sm, lineHeight: 1.5 }}>
                                  <div style={rowStyle}>
                                    <span style={labelStyle}>Transport:</span>{' '}{m.transport}
                                    {' · '}
                                    <span style={labelStyle}>Cycle:</span>{' '}{m.cycle}
                                  </div>
                                  <div style={rowStyle}>
                                    <span style={labelStyle}>Segment strategy:</span>{' '}{m.segmentStrategy}
                                  </div>
                                  <div style={rowStyle}>
                                    <span style={labelStyle}>Fields:</span>{' '}{m.fieldsFetched.join(', ')}
                                  </div>
                                  <div style={{ marginTop: 4, marginBottom: 2 }}>
                                    <span style={labelStyle}>Manual replication:</span>
                                  </div>
                                  <ol style={{ margin: '0 0 4px 0', paddingLeft: 20 }}>
                                    {m.manualSteps.map((step, i) => (
                                      <li key={i} style={{ marginBottom: 1 }}>{step}</li>
                                    ))}
                                  </ol>
                                  {m.notes && (
                                    <div style={{ ...rowStyle, color: theme.textSecondary }}>
                                      <span style={labelStyle}>Note:</span>{' '}{m.notes}
                                    </div>
                                  )}
                                  <div style={{ marginTop: 4 }}>
                                    <span style={labelStyle}>Scraper:</span>{' '}
                                    <code style={{ fontSize: designTokens.typography.fontSize.sm }}>{m.scraperCommand}</code>
                                  </div>
                                </div>
                              );
                            })()}
                          </dd>

                          <dt style={{ fontWeight: 600 }}>Geometry</dt>
                          <dd style={{ margin: 0 }}>
                            {p.segment_count?.toLocaleString() ?? '—'} segments,{' '}
                            {p.total_miles != null ? Number(p.total_miles).toLocaleString() : '—'} mi total
                          </dd>

                          <dt style={{ fontWeight: 600 }}>MAOP</dt>
                          <dd style={{ margin: 0 }}>
                            {p.maop_psig_max != null ? `${Number(p.maop_psig_max).toLocaleString()} psig` : '—'}
                          </dd>

                          <dt style={{ fontWeight: 600 }}>Notes</dt>
                          <dd style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{p.notes ?? '—'}</dd>
                        </dl>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
    </div>
  );
}
